import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { KeyRound, Plus, Trash2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

export function ApiKeysSettings() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/apikeys").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    const r = await api.post("/apikeys", { name: name.trim() || "JARVIS Key" });
    setNewKey(r.data.key); setName(""); load();
    toast.success("API key created — copy it now, it won't be shown again");
  };
  const remove = async (id) => { await api.delete(`/apikeys/${id}`); load(); };
  const copy = () => { navigator.clipboard?.writeText(newKey); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="rounded-3xl p-5 mb-4" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)" }} data-testid="apikeys-settings">
      <div className="flex items-center gap-2 mb-1"><KeyRound size={18} style={{ color: "var(--accent-primary)" }} /><h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>JARVIS API Keys</h3></div>
      <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>Give your external JARVIS assistant secure access to your calendar and mind maps. Send the key as <span className="font-mono">Authorization: Bearer &lt;key&gt;</span> to <span className="font-mono">{backendUrl}/api</span>.</p>

      {newKey && (
        <div className="mb-4 p-3 rounded-2xl" style={{ background: "color-mix(in srgb, var(--accent-primary) 12%, var(--bg-timeline-track))", border: "1px solid var(--border-active)" }}>
          <p className="text-[11px] uppercase tracking-wider mb-1.5" style={{ color: "var(--accent-primary)" }}>New key (copy now)</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs font-mono truncate px-2 py-1.5 rounded-lg" style={{ background: "var(--bg-app)", color: "var(--text-primary)" }} data-testid="new-apikey-value">{newKey}</code>
            <button onClick={copy} className="p-2 rounded-lg text-white" style={{ background: "var(--accent-primary)" }} data-testid="copy-apikey">{copied ? <Check size={15} /> : <Copy size={15} />}</button>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name (e.g., Home JARVIS)" className="flex-1 h-10 rounded-xl" style={{ background: "var(--bg-timeline-track)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} data-testid="apikey-name-input" />
        <Button onClick={create} className="h-10 text-white font-semibold" style={{ background: "var(--accent-primary)" }} data-testid="create-apikey"><Plus size={16} /> Generate</Button>
      </div>

      <div className="space-y-2">
        <AnimatePresence>
          {keys.map((k) => (
            <motion.div key={k.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: "var(--bg-timeline-track)", border: "1px solid var(--border-subtle)" }} data-testid="apikey-row">
              <KeyRound size={15} style={{ color: "var(--text-muted)" }} />
              <div className="flex-1 min-w-0"><p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{k.name}</p><p className="text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>{k.masked} · {k.last_used ? "used" : "never used"}</p></div>
              <button onClick={() => remove(k.id)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10" data-testid="delete-apikey"><Trash2 size={15} /></button>
            </motion.div>
          ))}
        </AnimatePresence>
        {keys.length === 0 && <p className="text-xs py-1" style={{ color: "var(--text-muted)" }}>No keys yet.</p>}
      </div>
    </div>
  );
}
